import { useEffect, useMemo, useState } from "react";
import { Modal, InputNumber, Input, Typography, message, Row, Col, Tag } from "antd";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { getActiveInventaireCodeId } from "../../../../variables/activeInventaire";
import InventaireSelectField from "../../../../components/inventaires/InventaireSelectField";
import { rightsService } from "../../../../services/rightsService";

const { Text } = Typography;
const fmt = (v) => (v || v === 0 ? Number(v).toLocaleString("fr-FR") : "0");

/**
 * Réévaluation d'UNE SEULE garantie (versionnement par garantie — Phase 2). Les autres garanties du
 * dossier sont reconduites à l'identique. Crée une nouvelle évaluation (à valider).
 * props : open, onClose, onSaved, sinistreCodeId, garantie {garantieCodeId, garantieNom, provision}
 */
const ReevaluationGarantieModal = ({ open, onClose, onSaved, sinistreCodeId, garantie }) => {
    const [principal, setPrincipal] = useState(0);
    const [honoraires, setHonoraires] = useState(0);
    const [motif, setMotif] = useState("");
    const [motifDetaille, setMotifDetaille] = useState("");
    const [inventaireCodeId, setInventaireCodeId] = useState(getActiveInventaireCodeId());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            // Pré-remplit avec le découpage courant si connu (principal/honoraires de la sous-évaluation
            // la plus récente) ; sinon replie principal = provision totale, honoraires = 0.
            const hasSplit = garantie?.principal != null || garantie?.honoraires != null;
            setPrincipal(Number(hasSplit ? garantie?.principal : garantie?.provision) || 0);
            setHonoraires(Number(hasSplit ? garantie?.honoraires : 0) || 0);
            setMotif("");
            setMotifDetaille("");
            setInventaireCodeId(getActiveInventaireCodeId());
        }
    }, [open, garantie]);

    const total = useMemo(() => (Number(principal) || 0) + (Number(honoraires) || 0), [principal, honoraires]);

    const enregistrer = () => {
        if (!motif || !motif.trim()) { message.warning("Le motif de réévaluation est requis."); return; }
        setSaving(true);
        axiosInstance.post(`/api/evaluation/reevaluer-garantie`, {
            sinistreCodeId,
            garantieCodeId: garantie?.garantieCodeId || null,
            garantieNom: garantie?.garantieNom || null,
            montantPrincipal: Number(principal) || 0,
            montantHonoraires: Number(honoraires) || 0,
            motif: motif.trim(),
            motifDetaille: motifDetaille || null,
            inventaireCodeId: inventaireCodeId || null,
        })
            .then(() => { message.success("Garantie réévaluée (à valider)."); onSaved && onSaved(); onClose && onClose(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec de la réévaluation."))
            .finally(() => setSaving(false));
    };

    return (
        <Modal open={open} onCancel={onClose} width={560} title="Réévaluer une garantie"
            okText="Réévaluer" onOk={enregistrer} confirmLoading={saving}>
            <div style={{ marginBottom: 12 }}>
                <Text>Garantie : <b>{garantie?.garantieNom || garantie?.garantieCodeId || "—"}</b></Text>
                <div><Text type="secondary">Provision courante : {fmt(garantie?.provision)} F</Text></div>
                <Text type="secondary" style={{ fontSize: 12 }}>Les autres garanties du dossier sont reconduites à l'identique.</Text>
            </div>
            <Row gutter={12}>
                <Col span={12}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Montant principal</div>
                    <InputNumber style={{ width: "100%" }} min={0} value={principal} onChange={setPrincipal}
                        formatter={(x) => `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} parser={(x) => (x || "").replace(/\s/g, "")} addonAfter="F" />
                </Col>
                <Col span={12}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Honoraires</div>
                    <InputNumber style={{ width: "100%" }} min={0} value={honoraires} onChange={setHonoraires}
                        formatter={(x) => `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} parser={(x) => (x || "").replace(/\s/g, "")} addonAfter="F" />
                </Col>
            </Row>
            <div style={{ margin: "10px 0" }}>
                <Tag color="purple">Nouvelle provision de la garantie : {fmt(total)} F</Tag>
            </div>
            <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Motif de réévaluation *</div>
                <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. Complément d'expertise" />
            </div>
            <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Observation / explication détaillée</div>
                <Input.TextArea rows={3} value={motifDetaille} onChange={(e) => setMotifDetaille(e.target.value)}
                    placeholder="Observation détaillée (facultatif)" />
            </div>
            {rightsService("HSI116") && (
                <div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Rattacher à un inventaire (optionnel)</div>
                    <InventaireSelectField value={inventaireCodeId} onChange={setInventaireCodeId} />
                </div>
            )}
        </Modal>
    );
};

export default ReevaluationGarantieModal;
